import React, { useMemo, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext"; // adjust path if needed

function getFullPath(loc) {
  return `${loc.pathname}${loc.search || ""}${loc.hash || ""}`;
}

export default function CreatorTopBar({
  profileTo = "/dashboard/profile",
  dashboardTo = "/dashboard/art",
  uploadTo = "/dashboard/art/new",

  showDashboard = true,
  showProfile = true,
  showUpload = true,

  activeKey,
  isEditing = false,
  onEditToggle,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const currentFullPath = useMemo(() => getFullPath(location), [location]);

  const isDashboard =
    activeKey === "dashboard" || location.pathname.startsWith("/dashboard/art");

  const isProfile =
    activeKey === "profile" ||
    location.pathname.startsWith("/dashboard/profile") ||
    location.pathname.startsWith("/creator"); // (optional) if your creator profile lives elsewhere

  // Capture the incoming state ONCE so it doesn't disappear after any navigation.
  const entryRef = useRef({
    from: location.state?.from,
    backTo:
      location.state?.backTo ||
      location.state?.returnTo ||
      location.state?.returnPath ||
      null,
  });

  // Back allowed in two cases:
  // 1) Opened from ArtHouse pages
  // 2) Opened dashboard from creator profile (so dashboard gets a Back button)
  const canGoBack = useMemo(() => {
    const from = entryRef.current.from;
    return (
      from === "arthouse-feed" ||
      from === "arthouse-gallery" ||
      from === "creator-profile"
    );
  }, []);

  const handleBack = () => {
    const target = entryRef.current.backTo;

    // If we have a valid target AND it's not the current route, go there.
    if (typeof target === "string" && target.trim() && target !== currentFullPath) {
      navigate(target); // don't replace; keep history sane
      return;
    }

    // Otherwise fall back to browser history.
    // This covers cases where state wasn't passed, or target accidentally equals current page.
    navigate(-1);
  };

  const handleLogout = async () => {
    try {
      await logout?.();
    } catch (e) {
      console.error("Logout failed:", e);
    } finally {
      navigate("/login");
    }
  };

  const baseBtn =
    "h-11 px-4 rounded-xl border transition text-sm font-semibold flex items-center gap-2";
  const ghost =
    "border-white/12 bg-white/[0.04] hover:bg-white/[0.08] text-white/80";
  const active =
    "border-sky-300/25 bg-sky-300/12 hover:bg-sky-300/16 text-sky-200";
  const primary =
    "border-sky-300/25 bg-sky-300/10 hover:bg-sky-300/15 text-sky-200";

  const logoutBtn =
    "border-white/12 bg-white/[0.04] text-white/75 hover:bg-rose-500/10 hover:border-rose-300/25 hover:text-rose-200";

  const profileLabel = isProfile ? (isEditing ? "Close Edit" : "Edit Profile") : "Profile";
  const profileActsAsToggle = isProfile && typeof onEditToggle === "function";

  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Back */}
        {canGoBack ? (
          <button
            onClick={handleBack}
            className="
              h-10 px-3 rounded-xl
              border border-white/12 bg-white/[0.04]
              hover:bg-white/[0.08] transition
              text-sm font-semibold text-white/75
              inline-flex items-center gap-2
            "
            aria-label="Go back"
          >
            <span className="text-lg leading-none">←</span>
            Back
          </button>
        ) : (
          <div />
        )}

        {/* Right side (hidden on dashboard) */}
        {isDashboard ? null : (
          <div className="flex items-center gap-2">
            {showDashboard && (
              <Link
                to={dashboardTo}
                // Pass full path so back goes to the exact prior page (incl search/hash)
                state={{ from: "creator-profile", backTo: currentFullPath }}
                className={`${baseBtn} ${ghost}`}
              >
                <span className="opacity-85">▣</span>
                Dashboard
              </Link>
            )}

            {showUpload && isProfile && (
              <Link to={uploadTo} className={`${baseBtn} ${primary}`}>
                <span className="opacity-85">⇪</span>
                Upload
              </Link>
            )}

            {showProfile &&
              (profileActsAsToggle ? (
                <button
                  type="button"
                  onClick={onEditToggle}
                  aria-pressed={isEditing}
                  className={`${baseBtn} ${isProfile ? active : ghost}`}
                >
                  {profileLabel}
                </button>
              ) : (
                <Link to={profileTo} className={`${baseBtn} ${isProfile ? active : ghost}`}>
                  {profileLabel}
                </Link>
              ))}

            <button
              type="button"
              onClick={handleLogout}
              className={`${baseBtn} ${logoutBtn}`}
              aria-label="Log out"
              title="Log out"
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
